import { isSoldOut } from '@/lib/stock';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Star } from 'lucide-react';
import corpo from '@/assets/home-v2/corpo-bio.webp.asset.json';
import maos from '@/assets/home-v2/maos-bio.webp.asset.json';
import pes from '@/assets/home-v2/pes-bio.webp.asset.json';
import sab from '@/assets/home-v2/sab-bio.webp.asset.json';
import bal from '@/assets/home-v2/bal-pack.webp.asset.json';
import kg from '@/assets/home-v2/kg-bio.webp.asset.json';
import { Button } from '@/components/ui/button';
import { getProductByShopifyId } from '@/data/productsMapping';
import { reviewsService } from '@/services/reviews/reviewsService';
import type { ShopifyProduct } from '@/lib/shopify';
import { cn } from '@/lib/utils';
import { useAddToCart, useProductsByHandle, useViewItemList, PriceBlock, Reveal, Eyebrow, H2 } from './shared';
import { PurchaseBenefits } from './PurchaseBenefits';

type ProductTone = 'corpo' | 'maos' | 'pes' | 'sab' | 'bal';

const PRODUCTS: Array<{ handle: string; image: string; tone: ProductTone }> = [
  { handle: 'hidratante-corporal-dermoconstrutor-80g', image: corpo.url, tone: 'corpo' },
  { handle: 'hidratante-para-as-maos-dermoconstrutor-80g', image: maos.url, tone: 'maos' },
  { handle: 'hidratante-para-os-pes-dermoconstrutor-80g', image: pes.url, tone: 'pes' },
  { handle: 'sabonete-liquido-extra-suave-150ml', image: sab.url, tone: 'sab' },
  { handle: 'balsamo-hidratante-corpo-e-rosto-30ml', image: bal.url, tone: 'bal' },
  { handle: 'hidratante-corporal-dermoconstrutor-1kg', image: kg.url, tone: 'corpo' },
];

const STRIPE: Record<ProductTone, string> = {
  corpo: 'bg-v2-corpo',
  maos: 'bg-v2-maos',
  pes: 'bg-v2-pes',
  sab: 'bg-v2-sab',
  bal: 'bg-v2-bal',
};

type Ratings = Record<string, { avg: number; count: number }>;

function getRating(product: ShopifyProduct | undefined, ratings: Ratings) {
  if (!product) return null;
  const numericId = product.node.id.split('/').pop();
  if (!numericId) return null;
  const mapping = getProductByShopifyId(numericId);
  if (!mapping) return null;
  return ratings[mapping.reviewsKonfidencyId] ?? null;
}

const ProductRating = ({ rating }: { rating: { avg: number; count: number } | null }) => {
  if (!rating || rating.count <= 0) return null;
  return (
    <div className="flex items-center gap-1.5 text-sm" aria-label={`${rating.avg.toFixed(1)} de 5 estrelas em ${rating.count} avaliações`}>
      <span className="flex" aria-hidden>
        {[1, 2, 3, 4, 5].map((value) => (
          <Star
            key={value}
            className={cn('h-3.5 w-3.5 text-brand-amber', value <= Math.round(rating.avg) && 'fill-brand-amber')}
          />
        ))}
      </span>
      <span className="font-semibold">{rating.avg.toFixed(1).replace('.', ',')}</span>
      <span className="text-v2-muted">({rating.count})</span>
    </div>
  );
};

const BestSellerCard = ({
  product,
  image,
  tone,
  rating,
  onAdd,
}: {
  product?: ShopifyProduct;
  image: string;
  tone: ProductTone;
  rating: { avg: number; count: number } | null;
  onAdd: (product: ShopifyProduct | undefined) => void;
}) => {
  if (!product) return <div className="aspect-[4/5] rounded-2xl bg-v2-ink/5 animate-pulse" />;

  return (
    <article className="group relative flex min-w-0 flex-col overflow-hidden rounded-2xl border border-v2-ink/10 bg-background">
      <Link
        to={`/produto/${product.node.handle}`}
        aria-label={`Ver ${product.node.title}`}
        className="absolute inset-0 z-10 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
      />
      <div className="aspect-[4/5] overflow-hidden bg-v2-bg">
        <img
          src={image}
          alt={product.node.title}
          width={768}
          height={960}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
      </div>
      <div className={cn('h-1 w-full', STRIPE[tone])} aria-hidden />
      <div className="flex flex-1 flex-col p-3 md:p-5">
        <h3 className="font-narrow text-lg font-semibold leading-tight md:text-xl">{product.node.title}</h3>
        <ProductRating rating={rating} />
        {tone === 'bal' && !isSoldOut(product.node) && (
          <div className="relative z-20 mt-3 space-y-2 text-sm">
            <p className="leading-snug text-v2-muted">Com óleos essenciais de lavanda e palma-rosa</p>
            <span className="inline-flex rounded-full bg-v2-bal/10 px-2.5 py-1 font-semibold text-v2-bal">Últimas unidades deste lote</span>
          </div>
        )}
        <PriceBlock product={product} className="mt-3" />
        {isSoldOut(product.node) ? (
        <Button asChild variant="outline" className="relative z-20 mt-4 min-h-12 w-full whitespace-normal rounded-full px-2 text-sm leading-tight sm:px-3">
          <Link to={`/produto/${product.node.handle}`}>Esgotado · Avise-me</Link>
        </Button>
        ) : (
        <Button
          type="button"
          onClick={() => onAdd(product)}
          className="relative z-20 mt-4 min-h-12 w-full whitespace-normal rounded-full px-2 text-sm leading-tight sm:px-3"
        >
          <ShoppingBag className="h-4 w-4" />
          Adicionar ao carrinho
        </Button>
        )}
        <PurchaseBenefits className="relative z-20" />
      </div>
    </article>
  );
};

export const BestSellersV2 = () => {
  const { map } = useProductsByHandle();
  const addToCart = useAddToCart();
  const [ratings, setRatings] = useState<Ratings>({});
  const products = PRODUCTS.map((item) => map.get(item.handle));
  useViewItemList('home_mais_vendidos', products);

  useEffect(() => {
    let active = true;
    reviewsService.getAllProductRatings().then((result) => {
      if (active) setRatings(result);
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  return (
    <Reveal as="section" className="bg-background py-16 text-v2-ink md:py-24">
      <div className="container mx-auto max-w-6xl px-5">
        <Eyebrow>Os preferidos de quem tem pele sensível</Eyebrow>
        <H2>Mais vendidos</H2>
        <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5">
          {PRODUCTS.map((item, index) => (
            <BestSellerCard
              key={item.handle}
              product={products[index]}
              image={item.image}
              tone={item.tone}
              rating={getRating(products[index], ratings)}
              onAdd={addToCart}
            />
          ))}
        </div>
      </div>
    </Reveal>
  );
};